import { styled } from '@mui/material/styles';
import { 
  Box, 
  Paper, 
  TextField, 
  Typography, 
  Button, 
  Container,
  Card,
  CardContent,
  CardMedia,
  IconButton,
  Slider,
  ToggleButtonGroup,
  ToggleButton,
  Alert
} from '@mui/material';

// Honey-inspired color palette
export const honeyGeneralColors = {
  goldenHoney: '#F1C40F',
  brightHoney: '#F4D03F',
  honeycomb: '#D4A574',
  darkHoney: '#B8860B',
  honeyBrown: '#5D4E37',
  softCream: '#FFFEF7',
  warmAmber: '#CD853F',
  lightBrown: '#8B7355',
  honeyGradient: 'linear-gradient(135deg, #F1C40F, #F4D03F)',
  honeycombGradient: 'linear-gradient(135deg, #D4A574, #F1C40F)',
};

// Main container
export const HoneyGeneralContainer = styled(Container)(({ theme }) => ({
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  color: honeyGeneralColors.honeyBrown,
  paddingTop: theme.spacing(3),
  paddingBottom: theme.spacing(3),
}));

// Header section
export const HoneyGeneralHeader = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  marginBottom: theme.spacing(4),
}));

// Title with honey gradient
export const HoneyGeneralTitle = styled(Typography)(({ theme }) => ({
  fontSize: '2.5rem',
  fontWeight: 700,
  marginBottom: theme.spacing(0.5),
  background: honeyGeneralColors.honeycombGradient,
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1),
}));

// Subtitle
export const HoneyGeneralSubtitle = styled(Typography)(({ theme }) => ({
  color: honeyGeneralColors.lightBrown,
  fontSize: '1.1rem',
  fontWeight: 400,
}));

// Section container
export const HoneySectionContainer = styled(Paper)(({ theme }) => ({
  background: 'white',
  borderRadius: '20px',
  border: `2px solid ${honeyGeneralColors.brightHoney}`,
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  position: 'relative',
  overflow: 'hidden',
  
  // Honey accent stripe
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: `linear-gradient(135deg, ${honeyGeneralColors.goldenHoney} 0%, ${honeyGeneralColors.brightHoney} 50%, ${honeyGeneralColors.honeycomb} 100%)`,
    borderRadius: '20px 20px 0 0',
  },
}));

// Form field styling
export const HoneyTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    background: 'white',
    transition: 'all 0.3s ease',
    
    '& fieldset': {
      borderColor: honeyGeneralColors.brightHoney,
      borderWidth: '2px',
    },
    
    '&:hover fieldset': {
      borderColor: honeyGeneralColors.goldenHoney,
    },
    
    '&.Mui-focused fieldset': {
      borderColor: honeyGeneralColors.goldenHoney,
      boxShadow: `0 0 0 3px ${honeyGeneralColors.goldenHoney}20`,
    },
  },
  
  '& .MuiInputBase-input': {
    fontSize: '1rem',
    color: honeyGeneralColors.honeyBrown,
    padding: theme.spacing(1.5),
    
    '&::placeholder': {
      color: honeyGeneralColors.lightBrown,
    },
  },
  
  '& .MuiInputLabel-root': {
    color: honeyGeneralColors.honeyBrown,
    fontWeight: 600,
    fontSize: '0.95rem',
    
    '&.Mui-focused': {
      color: honeyGeneralColors.goldenHoney,
    },
  },
  
  '& .MuiFormHelperText-root': {
    fontSize: '0.85rem',
    color: honeyGeneralColors.lightBrown,
    fontStyle: 'italic',
  },
}));

// Editor container
export const HoneyEditorContainer = styled(Paper)(({ theme }) => ({
  border: `2px solid ${honeyGeneralColors.brightHoney}`,
  borderRadius: '12px',
  padding: theme.spacing(2),
  minHeight: '200px',
  background: 'white',
  
  '& .wysiwyg-wrapper': {
    border: 'none',
  },
  
  '& .wysiwyg-editor': {
    border: 'none',
    padding: theme.spacing(1),
    minHeight: '150px',
    color: honeyGeneralColors.honeyBrown,
  },
  
  '& .rdw-editor-toolbar': {
    borderBottom: `1px solid ${honeyGeneralColors.brightHoney}`,
    marginBottom: theme.spacing(1),
  },
}));

// Section title
export const HoneySectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.4rem',
  fontWeight: 600,
  color: honeyGeneralColors.honeyBrown,
  marginBottom: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

// Image upload card
export const HoneyImageCard = styled(Card)(({ theme }) => ({
  borderRadius: '16px',
  border: `2px solid ${honeyGeneralColors.brightHoney}`,
  boxShadow: `0 4px 20px ${honeyGeneralColors.goldenHoney}20`,
  transition: 'all 0.3s ease',
  
  '&:hover': {
    borderColor: honeyGeneralColors.goldenHoney,
    boxShadow: `0 8px 30px ${honeyGeneralColors.goldenHoney}30`,
    transform: 'translateY(-2px)',
  },
}));

// Image upload area
export const HoneyImageUploadArea = styled(Box)(({ theme }) => ({
  height: 200,
  border: `2px dashed ${honeyGeneralColors.brightHoney}`,
  borderRadius: '12px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  background: `linear-gradient(135deg, ${honeyGeneralColors.softCream}, white)`,
  
  '&:hover': {
    backgroundColor: `${honeyGeneralColors.brightHoney}10`,
    borderColor: honeyGeneralColors.goldenHoney,
  },
}));

// Image preview
export const HoneyImagePreview = styled(CardMedia)(({ theme }) => ({
  height: 200,
  borderRadius: '12px',
  position: 'relative',
}));

// Image delete button
export const HoneyImageDeleteButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: 8,
  right: 8,
  backgroundColor: 'rgba(0,0,0,0.7)',
  color: 'white',
  borderRadius: '50%',
  transition: 'all 0.3s ease',
  
  '&:hover': {
    backgroundColor: honeyGeneralColors.warmAmber,
    transform: 'scale(1.1)',
  },
}));

// Status toggle group
export const HoneyStatusToggle = styled(ToggleButtonGroup)(({ theme }) => ({
  borderRadius: '50px',
  overflow: 'hidden',
  border: `2px solid ${honeyGeneralColors.brightHoney}`,
  
  '& .MuiToggleButton-root': {
    borderRadius: '0',
    border: 'none',
    padding: theme.spacing(1.5, 3),
    fontWeight: 600,
    transition: 'all 0.3s ease',
    
    '&:not(:last-of-type)': {
      borderRight: `1px solid ${honeyGeneralColors.brightHoney}`,
    },
    
    '&.Mui-selected': {
      background: honeyGeneralColors.honeyGradient,
      color: honeyGeneralColors.honeyBrown,
      
      '&:hover': {
        background: honeyGeneralColors.honeycombGradient,
      },
    },
    
    '&:hover': {
      backgroundColor: `${honeyGeneralColors.brightHoney}20`,
    },
  },
}));

// Slider container
export const HoneySliderContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  
  '& .MuiSlider-root': {
    color: honeyGeneralColors.goldenHoney,
    height: 8,
    
    '& .MuiSlider-track': {
      background: honeyGeneralColors.honeyGradient,
      border: 'none',
    },
    
    '& .MuiSlider-thumb': {
      height: 24,
      width: 24,
      backgroundColor: honeyGeneralColors.goldenHoney,
      border: `3px solid ${honeyGeneralColors.brightHoney}`,
      boxShadow: `0 0 0 8px ${honeyGeneralColors.goldenHoney}20`,
      
      '&:hover': {
        boxShadow: `0 0 0 8px ${honeyGeneralColors.goldenHoney}30`,
      },
    },
    
    '& .MuiSlider-markLabel': {
      color: honeyGeneralColors.honeyBrown,
      fontSize: '0.8rem',
      fontWeight: 500,
    },
    
    '& .MuiSlider-mark': {
      backgroundColor: honeyGeneralColors.honeycomb,
      height: 4,
      width: 4,
    },
  },
}));

// Slider value display
export const HoneySliderValue = styled(Typography)(({ theme }) => ({
  textAlign: 'center',
  marginTop: theme.spacing(2),
  fontSize: '1.1rem',
  fontWeight: 600,
  color: honeyGeneralColors.honeyBrown,
  
  '& strong': {
    color: honeyGeneralColors.goldenHoney,
    fontSize: '1.3rem',
  },
}));

// Save button
export const HoneySaveButton = styled(Button)(({ theme }) => ({
  background: honeyGeneralColors.honeyGradient,
  color: honeyGeneralColors.honeyBrown,
  borderRadius: '50px',
  padding: theme.spacing(1.5, 4),
  fontWeight: 700,
  fontSize: '1.1rem',
  boxShadow: `0 8px 25px ${honeyGeneralColors.goldenHoney}40`,
  transition: 'all 0.3s ease',
  textTransform: 'none',
  minWidth: '200px',
  
  '&:hover': {
    background: honeyGeneralColors.honeycombGradient,
    transform: 'translateY(-2px)',
    boxShadow: `0 12px 35px ${honeyGeneralColors.goldenHoney}60`,
  },
  
  '&:disabled': {
    opacity: 0.6,
    transform: 'none',
    background: honeyGeneralColors.honeyGradient,
  },
}));

// Actions container
export const HoneyActionsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(2),
}));

// Alert styling
export const HoneyAlert = styled(Alert)(({ theme }) => ({
  borderRadius: '12px',
  border: `1px solid ${honeyGeneralColors.brightHoney}`,
  
  '&.MuiAlert-standardSuccess': {
    backgroundColor: `#4caf5015`,
    borderColor: '#4caf50',
    color: honeyGeneralColors.honeyBrown,
    
    '& .MuiAlert-icon': {
      color: '#4caf50',
    },
  },
  
  '&.MuiAlert-standardWarning': {
    backgroundColor: `${honeyGeneralColors.warmAmber}15`,
    borderColor: honeyGeneralColors.warmAmber,
    color: honeyGeneralColors.honeyBrown,
    
    '& .MuiAlert-icon': {
      color: honeyGeneralColors.warmAmber,
    },
  },
  
  '&.MuiAlert-standardError': {
    backgroundColor: `#f4433615`,
    borderColor: '#f44336',
    color: honeyGeneralColors.honeyBrown,
    
    '& .MuiAlert-icon': {
      color: '#f44336',
    },
  },
}));

// Map container
export const HoneyMapContainer = styled(Box)(({ theme }) => ({
  borderRadius: '16px',
  overflow: 'hidden',
  border: `2px solid ${honeyGeneralColors.brightHoney}`,
  boxShadow: `0 4px 20px ${honeyGeneralColors.goldenHoney}20`,
}));

// Divider
export const HoneyDivider = styled(Box)(({ theme }) => ({
  height: '2px',
  background: `linear-gradient(90deg, transparent, ${honeyGeneralColors.brightHoney}, transparent)`,
  margin: theme.spacing(4, 0),
}));